import { ButtonColor, ButtonSize, ButtonVariant } from './buttons.constants';
import { IconSize } from '../icon/icon.constants';

/*
  Shared button styling maps used by both Button and IconButton. Keep every
  utility a complete literal string — Tailwind scans this file as text, so
  template-literal concatenation would not be detected.
*/

export const baseClass = [
  'inline-flex items-center justify-center shrink-0 cursor-pointer transition duration-200 ease-in-out',
  'not-disabled:focus-visible:[outline:2px_solid_currentColor] not-disabled:focus-visible:outline-offset-2',
  'disabled:cursor-default',
];

// Heights are fixed dimensions, hardcoded as rem (not from the spacing scale).
export const sizeClass: Record<ButtonSize, string> = {
  [ButtonSize.XS]: 'h-[1.5rem]', // 24px
  [ButtonSize.SM]: 'h-[2rem]', // 32px
  [ButtonSize.MD]: 'h-[2.5rem]', // 40px
  [ButtonSize.LG]: 'h-[3rem]', // 48px
};

export const minSizeClass: Record<ButtonSize, string> = {
  [ButtonSize.XS]: 'min-h-[1.5rem]',
  [ButtonSize.SM]: 'min-h-[2rem]',
  [ButtonSize.MD]: 'min-h-[2.5rem]',
  [ButtonSize.LG]: 'min-h-[3rem]',
};

// A compact Button is never narrower than it is tall, so a one-character label
// (a page number) renders as a square and longer labels still grow.
export const compactMinWidthClass: Record<ButtonSize, string> = {
  [ButtonSize.XS]: 'min-w-[1.5rem]',
  [ButtonSize.SM]: 'min-w-[2rem]',
  [ButtonSize.MD]: 'min-w-[2.5rem]',
  [ButtonSize.LG]: 'min-w-[3rem]',
};

// Glyph size paired to each button size (24→12, 32→16, 40→20, 48→24), so the
// icon scales in step with the button and callers never pick a mismatched size.
export const iconSizeForButton: Record<ButtonSize, IconSize> = {
  [ButtonSize.XS]: IconSize.XS,
  [ButtonSize.SM]: IconSize.SM,
  [ButtonSize.MD]: IconSize.MD,
  [ButtonSize.LG]: IconSize.LG,
};

// IconButton glyphs run one size larger than Button's (24→16, 32→20, 40→24,
// 48→28) so the icon fills roughly two thirds of the square.
export const iconSizeForIconButton: Record<ButtonSize, IconSize> = {
  [ButtonSize.XS]: IconSize.SM,
  [ButtonSize.SM]: IconSize.MD,
  [ButtonSize.MD]: IconSize.LG,
  [ButtonSize.LG]: IconSize.XL,
};

// Color per variant. The `text-*` utilities also color icon glyphs, which
// inherit `currentColor`.
export const colorClass: Record<ButtonVariant, Record<ButtonColor, string>> = {
  [ButtonVariant.FILLED]: {
    [ButtonColor.PRIMARY]:
      'bg-primary text-on-primary not-disabled:hover:bg-primary-hover not-disabled:active:bg-primary-active disabled:bg-primary-disabled',
    [ButtonColor.SECONDARY]:
      'bg-secondary text-on-secondary not-disabled:hover:bg-secondary-hover not-disabled:active:bg-secondary-active disabled:bg-secondary-disabled',
    [ButtonColor.TERTIARY]:
      'bg-tertiary text-on-tertiary not-disabled:hover:bg-tertiary-hover not-disabled:active:bg-tertiary-active disabled:bg-tertiary-disabled',
    [ButtonColor.SURFACE_CONTAINER]:
      'bg-surface-container text-on-surface-primary not-disabled:hover:bg-surface-container-hover not-disabled:active:bg-tertiary/20 disabled:bg-surface-container disabled:text-tertiary-disabled',
    [ButtonColor.SUCCESS]:
      'bg-success text-on-success not-disabled:hover:bg-success-hover not-disabled:active:bg-success-active disabled:bg-success-disabled',
    [ButtonColor.ERROR]:
      'bg-error text-on-error not-disabled:hover:bg-error-hover not-disabled:active:bg-error-active disabled:bg-error-disabled',
  },
  [ButtonVariant.OUTLINED]: {
    [ButtonColor.PRIMARY]:
      'border border-primary text-primary not-disabled:hover:bg-primary/20 not-disabled:active:bg-primary/40 disabled:border-primary-disabled disabled:text-primary-disabled',
    [ButtonColor.SECONDARY]:
      'border border-secondary text-secondary not-disabled:hover:bg-secondary/20 not-disabled:active:bg-secondary/40 disabled:border-secondary-disabled disabled:text-secondary-disabled',
    [ButtonColor.TERTIARY]:
      'border border-tertiary text-tertiary not-disabled:hover:bg-tertiary/20 not-disabled:active:bg-tertiary/40 disabled:border-tertiary-disabled disabled:text-tertiary-disabled',
    [ButtonColor.SURFACE_CONTAINER]:
      'border border-line bg-surface-container text-on-surface-primary not-disabled:hover:bg-surface-container-hover not-disabled:active:bg-tertiary/20 disabled:border-tertiary-disabled disabled:text-tertiary-disabled',
    [ButtonColor.SUCCESS]:
      'border border-success text-success not-disabled:hover:bg-success/20 not-disabled:active:bg-success/40 disabled:border-success-disabled disabled:text-success-disabled',
    [ButtonColor.ERROR]:
      'border border-error text-error not-disabled:hover:bg-error/20 not-disabled:active:bg-error/40 disabled:border-error-disabled disabled:text-error-disabled',
  },
  [ButtonVariant.TEXT]: {
    [ButtonColor.PRIMARY]:
      'text-primary not-disabled:hover:bg-primary/20 not-disabled:active:bg-primary/40 disabled:text-primary-disabled',
    [ButtonColor.SECONDARY]:
      'text-secondary not-disabled:hover:bg-secondary/20 not-disabled:active:bg-secondary/40 disabled:text-secondary-disabled',
    [ButtonColor.TERTIARY]:
      'text-tertiary not-disabled:hover:bg-tertiary/20 not-disabled:active:bg-tertiary/40 disabled:text-tertiary-disabled',
    [ButtonColor.SURFACE_CONTAINER]:
      'text-on-surface-primary not-disabled:hover:bg-surface-container-hover not-disabled:active:bg-tertiary/20 disabled:text-tertiary-disabled',
    [ButtonColor.SUCCESS]:
      'text-success not-disabled:hover:bg-success/20 not-disabled:active:bg-success/40 disabled:text-success-disabled',
    [ButtonColor.ERROR]:
      'text-error not-disabled:hover:bg-error/20 not-disabled:active:bg-error/40 disabled:text-error-disabled',
  },
};
